import { useCombobox } from "downshift";
import { DebouncedFunc, debounce } from "lodash";
import React, { useState, useRef, useContext, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { GeoFeature } from "utils/types";
import cx from "classnames";
import { AppContext } from "contexts/AppContext";

type LodashDebounceFunc = DebouncedFunc<
  (downshiftInput: { inputValue?: string }) => void
>;

interface AddressSelectProps {
  label: string;
  errorText?: string
  defaultValue?: string;
  required: string | boolean
}

/** L'API adresse.data.gouv.fr rejette les recherches de moins de 3 caractères */
const MIN_SEARCH_LENGTH = 3;
const MAX_SEARCH_LENGTH = 200;

export default function AddressSelect({
  label,
  errorText,
  defaultValue,
  required
}: AddressSelectProps) {
  const { setValue, register } = useFormContext();
  const { isMobile } = useContext(AppContext);
  const [adresses, setAdresses] = useState<GeoFeature[]>([]);
  const [isOptionSelected, setIsOptionSelected] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const debounceUserInput = useRef<LodashDebounceFunc | null>(null);
  // Permet d'annuler la requête précédente : sans ça, une réponse lente
  // peut écraser les résultats d'une saisie plus récente.
  const abortControllerRef = useRef<AbortController | null>(null);

  if (!debounceUserInput.current) {
    debounceUserInput.current = debounce(makeAdressCall, 500);
  }

  useEffect(() => {
    const debounced = debounceUserInput.current;
    return () => {
      debounced?.cancel();
      abortControllerRef.current?.abort();
    };
  }, []);

  function makeAdressCall(downshiftInput: { inputValue?: string }) {
    const trimmedValue = (downshiftInput.inputValue || "").trim();

    abortControllerRef.current?.abort();

    if (
      trimmedValue.length < MIN_SEARCH_LENGTH ||
      trimmedValue.length > MAX_SEARCH_LENGTH
    ) {
      // Rien à chercher : on nettoie les suggestions précédentes,
      // sinon des résultats obsolètes restent affichés.
      setAdresses([]);
      setSearchError(null);
      setIsSearching(false);
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setIsSearching(true);

    fetch(
      `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(trimmedValue)}`,
      { signal: controller.signal }
    )
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Recherche d'adresse en erreur (HTTP ${res.status})`);
        }
        return res.json();
      })
      .then((res: { features?: GeoFeature[] }) => {
        setAdresses(res?.features ?? []);
        setSearchError(null);
        setIsSearching(false);
      })
      .catch((error: unknown) => {
        // Une requête annulée n'est pas une erreur : une plus récente l'a remplacée.
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        console.error('Error fetching address data', error);
        setAdresses([]);
        setIsSearching(false);
        setSearchError(
          "La recherche d'adresse est momentanément indisponible. Vous pouvez réessayer dans quelques instants."
        );
      });
  }
  const hasError = !!errorText;

  const {
    isOpen,
    inputValue,
    getMenuProps,
    getInputProps,
    getItemProps,
  } = useCombobox({
    onInputValueChange: debounceUserInput.current,
    items: adresses,
    itemToString(item) {
      return item ? item.properties.label : "";
    },
    defaultInputValue: defaultValue || "",
    onIsOpenChange: (changes) => {
      if (!changes.selectedItem) {
        setValue("postalCode", "");
        setValue("street", "");
        setValue("city", "");
        setValue("housenumber", "" || "",);
        setValue("address", changes.inputValue || "", { shouldDirty: true });
        setIsOptionSelected(false);
      }
    },
    onSelectedItemChange(changes) {
      setValue("postalCode", changes.selectedItem?.properties.postcode || "");
      setValue("street", changes.selectedItem?.properties.street || "");
      setValue("city", changes.selectedItem?.properties.city || "");
      setValue(
        "housenumber",
        changes.selectedItem?.properties.housenumber || "",
      );
      setValue(
        "address",
        changes.selectedItem?.properties.label || "",
        { shouldDirty: true }
      );
      setIsOptionSelected(true);
    },
  });

  const validateAddress = () => {
    if (!isOptionSelected) return "Adresse non sélectionnée";
    return true;
  }

  const trimmedInput = (inputValue || "").trim();
  const isInputTooShort =
    trimmedInput.length > 0 && trimmedInput.length < MIN_SEARCH_LENGTH;
  const isInputTooLong = trimmedInput.length > MAX_SEARCH_LENGTH;
  const hasNoResult =
    !isSearching &&
    !searchError &&
    !isInputTooShort &&
    !isInputTooLong &&
    trimmedInput.length >= MIN_SEARCH_LENGTH &&
    adresses.length === 0;
  const hasMessage =
    isInputTooShort || isInputTooLong || isSearching || !!searchError || hasNoResult;

  return (
    <div
      className={cx("input-container address", {
        mobile: isMobile,
        errored: hasError
      })}
    >
      <input type="hidden" {...register("postalCode")} />
      <input type="hidden" {...register("street")} />
      <input type="hidden" {...register("housenumber")} />
      <label htmlFor="address">
        <input
          id="address"
          type="text"
          placeholder=" "
          className="input"
          {...register("address", {
            validate: validateAddress,
            required,
          })}
          {...getInputProps()}
        />
        <span>{label}</span>
      </label>
      {errorText ? <span className="error">{errorText}</span> : null}
      <ul
        {...getMenuProps()}
        className="search-box"
      >
        {isOpen && hasMessage && (
          <li className="search-message">
            {isInputTooShort && (
              <span>Saisissez au moins {MIN_SEARCH_LENGTH} caractères pour lancer la recherche.</span>
            )}
            {isInputTooLong && (
              <span>Votre saisie est trop longue pour lancer une recherche.</span>
            )}
            {isSearching && !isInputTooShort && !isInputTooLong && (
              <span>Recherche en cours…</span>
            )}
            {!!searchError && !isSearching && (
              <span className="search-error">{searchError}</span>
            )}
            {hasNoResult && <span>Aucune adresse ne correspond à votre recherche.</span>}
          </li>
        )}
        {isOpen && adresses?.length > 0 &&
          adresses.map((item, index) => (
            <li
              key={`${item.properties.id}`}
              {...getItemProps({ item, index })}
            >
              <span>{item.properties.label}</span>
            </li>
          ))}
      </ul>
    </div>
  )
}
